import React from "react";
import { useTranslation } from "react-i18next";
import Avatar from "react-avatar";
import Badge from "../../../../../components/Badge";
import Spinner from "../../../../../components/Spinner";
import ElipsisText from "../../../../../components/ElipsisText";
import Link from "../../../../../components/Link";
import { ManagerStatement } from "../../../../../interfaces/managerStatement.interface";
import { AuthRoutes, Modules, RouteParamsTypes } from "../../../../../enums";
import { extractDateFromDateTimeBySpace } from "../../../../../utils/methods";
import { CLEANING_MARGIN_PERCENTAGE } from "../../../../../utils/appConstant";

interface PrimaryDetailsProps {
  loading: boolean;
  statement: ManagerStatement | null;
  period: string;
  statusOptions: any[];
}

const formatPercentage = (value: number | null | undefined) => (
  value !== null && value !== undefined ? `${value}%` : '-'
);

const STATUS_VARIANT_MAP: Record<string, 'secondary' | 'info' | 'warning' | 'success' | 'primary'> = {
  draft: 'secondary',
  ready_for_review: 'info',
  need_clarification: 'warning',
  completed: 'success',
  paid: 'primary',
};

const getStatusVariant = (status?: string) => (status && STATUS_VARIANT_MAP[status]) || 'info';

const PrimaryDetails: React.FC<PrimaryDetailsProps> = ({
  loading, statement, period, statusOptions,
}) => {
  const { t } = useTranslation();

  if (loading) {
    return (
      <div className="card mb-6">
        <div className="card-body text-center">
          <Spinner />
        </div>
      </div>
    );
  }

  if (!statement) return null;

  return (
    <div className="card mb-6">
      <div className="card-body position-relative">
        <div className="d-flex justify-content-end align-items-center position-absolute" style={{ top: 0, right: 0 }}>
          <Badge
            className="px-3 py-2 fw-medium fs-6"
            variant={getStatusVariant(statement.status)}
            style={{ borderRadius: '0px 10px 0px 10px' }}
            text={(statusOptions || []).find((option) => option.id === statement.status)?.name || statement.status}
          />
        </div>

        <div className="d-flex justify-content-between align-items-center">
          <div className="col-8">
            <div className="d-flex align-items-center">
              <div className="me-2" style={{ width: 50 }}>
                <Avatar name={statement.manager_name || ''} size="50" maxInitials={2} round />
              </div>
              <div style={{ width: 'calc(100% - 60px)' }}>
                <ElipsisText
                  text={statement.manager_name || '-'}
                  node={
                    <Link
                      text={statement.manager_name || '-'}
                      href={`${Modules.resources}${AuthRoutes.users}${RouteParamsTypes.view}/${statement.manager_id}`}
                      target="_blank"
                    />
                  }
                  className="mb-0 app-brand-text fw-semibold demo"
                  width="100%"
                />
                <p className="mb-0 text-muted">{t("reconciliation.managerStatement.detail.pageTexts.fields.role")}</p>
              </div>
            </div>
          </div>
          <div className="col-4 text-center">
            <h5 className="mb-0">{statement.statement_number || '-'}</h5>
          </div>
        </div>

        <hr className="my-4 mx-n4" />

        <div className="d-flex justify-content-between align-items-center flex-wrap">
          <div>
            <p className="mb-0"><b>{period || '-'}</b></p>
            <p className="mb-0">{t("reconciliation.managerStatement.detail.pageTexts.fields.period")}</p>
          </div>
          <div className='border-end'>
            &nbsp; <br /> &nbsp;
          </div>
          <div>
            <p className="mb-0"><b>{statement.created_at ? extractDateFromDateTimeBySpace(statement.created_at) : '-'}</b></p>
            <p className="mb-0">{t("reconciliation.managerStatement.detail.pageTexts.fields.statementDate")}</p>
          </div>
          <div className='border-end'>
            &nbsp; <br /> &nbsp;
          </div>
          <div>
            <div className="mb-0">
              <b>{formatPercentage(statement.base_commission_percentage)}</b>
            </div>
            <p className="mb-0">{t("reconciliation.managerStatement.detail.pageTexts.fields.baseCommission")}</p>
          </div>
          <div className='border-end'>
            &nbsp; <br /> &nbsp;
          </div>
          <div>
            <div className="mb-0">
              <b>{formatPercentage(statement.property_visit_commission_percentage)}</b>
            </div>
            <p className="mb-0">{t("reconciliation.managerStatement.detail.pageTexts.fields.visitCommission")}</p>
          </div>
          <div className='border-end'>
            &nbsp; <br /> &nbsp;
          </div>
          <div>
            <div className="mb-0">
              <b>{formatPercentage(statement.transaction_sharing_percentage)}</b>
            </div>
            <p className="mb-0">{t("reconciliation.managerStatement.detail.pageTexts.fields.transactionSharing")}</p>
          </div>
          <div className='border-end'>
            &nbsp; <br /> &nbsp;
          </div>
          <div>
            <div className="mb-0">
              <b>{formatPercentage(CLEANING_MARGIN_PERCENTAGE)}</b>
            </div>
            <p className="mb-0">{t("reconciliation.managerStatement.detail.pageTexts.fields.cleaningMargin")}</p>
          </div>
          <div></div>
        </div>
      </div>
    </div>
  );
};

export default PrimaryDetails;
